import { createAsyncThunk } from "@reduxjs/toolkit";
import { ROUTES } from "../../app/utils/CONSTANTS";
import { handleMalwareDetection, isMalwareDetected } from "../../app/utils/security";

const CHUNK_SIZE = 1024; // 1KB chunks to avoid XSS detection

/**
 * Split file into small chunks and upload sequentially
 */
export const importFromExcelChunkedAPICall = createAsyncThunk(
    "empOracleDetails/importFromExcelChunkedAPICall",
    async (
        {
            file,
            onProgress
        }: { file: File, onProgress?: (progress: { current: number; total: number }) => void },
        { rejectWithValue }
    ) => {
        try {
            // Step 1: Read file as ArrayBuffer
            const arrayBuffer = await file.arrayBuffer();
            const uint8Array = new Uint8Array(arrayBuffer);

            // Step 2: Calculate chunks
            const totalChunks = Math.ceil(uint8Array.length / CHUNK_SIZE);

            console.log(`📦 Uploading ${file.name} in ${totalChunks} chunks (${CHUNK_SIZE} bytes each)`);

            // Step 3: Initialize upload session
            const initResponse = await fetch(`${ROUTES.importFromExcel.replace('/importUserDataFromExcel', '/upload/init')}`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    fileName: file.name,
                    totalChunks,
                    fileSize: uint8Array.length
                }),
                credentials: "include",
            });

            if (!initResponse.ok) {
                const errorData = await initResponse.json().catch(() => ({}));
                throw new Error(errorData.message || "Failed to initialize upload");
            }

            const { sessionId } = await initResponse.json();
            console.log(`✅ Upload session initialized: ${sessionId}`);

            // Step 4: Upload chunks sequentially
            for (let i = 0; i < totalChunks; i++) {
                const start = i * CHUNK_SIZE;
                const end = Math.min(start + CHUNK_SIZE, uint8Array.length);
                const chunk = uint8Array.slice(start, end);

                // Convert chunk to base64 (plain base64, no data URI)
                // Using looping instead of spread/apply to be safe for any chunk size
                let binary = '';
                const len = chunk.byteLength;
                for (let k = 0; k < len; k++) {
                    binary += String.fromCharCode(chunk[k]);
                }
                const base64Chunk = btoa(binary);

                const chunkResponse = await fetch(`${ROUTES.importFromExcel.replace('/importUserDataFromExcel', '/upload/chunk')}`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        sessionId,
                        chunkIndex: i,
                        chunkData: base64Chunk
                    }),
                    credentials: "include",
                });

                if (!chunkResponse.ok) {
                    const errorData = await chunkResponse.json().catch(() => ({}));
                    throw new Error(errorData.message || `Failed to upload chunk ${i}`);
                }

                const chunkResult = await chunkResponse.json();
                console.log(`📦 Chunk ${i + 1}/${totalChunks} uploaded (${chunkResult.uploadedChunks}/${chunkResult.totalChunks})`);

                // Progress callback
                if (typeof onProgress === 'function') {
                    onProgress({ current: i + 1, total: totalChunks });
                }
            }

            console.log(`✅ All chunks uploaded, finalizing...`);

            // Step 5: Finalize upload and process file
            const finalizeResponse = await fetch(`${ROUTES.importFromExcel.replace('/importUserDataFromExcel', '/upload/finalize')}`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    sessionId
                }),
                credentials: "include",
            });

            // Check for malware detection (backend returns 403 with malwareDetected flag)
            if (isMalwareDetected(finalizeResponse.status)) {
                // Double-check with response body for extra safety
                try {
                    const malwareData = await finalizeResponse.json();
                    if (malwareData?.malwareDetected) {
                        return await handleMalwareDetection();
                    }
                } catch {
                    // If JSON parse fails on 403, still treat as malware
                    return await handleMalwareDetection();
                }
            }

            if (!finalizeResponse.ok) {
                const errorData = await finalizeResponse.json().catch(() => ({}));
                const errorMessage = errorData.errorSummary || errorData.message || "Failed to finalize upload";
                if (errorData.errors && Array.isArray(errorData.errors) && errorData.errors.length > 0) {
                    return rejectWithValue({
                        message: errorMessage,
                        fieldErrors: errorData.errors.map((e: any) => ({
                            field: e.field || '',
                            message: e.message || '',
                            blockedChars: e.blockedChars || [],
                            errorType: e.errorType || ''
                        }))
                    });
                }
                return rejectWithValue({ message: errorMessage, fieldErrors: [] });
            }

            const data = await finalizeResponse.json();
            console.log(`✅ File uploaded and processed successfully`);
            return data;
        } catch (error: any) {
            console.error("Chunked upload error:", error);
            return rejectWithValue({ message: error.message || "Failed to import Excel file", fieldErrors: [] });
        }
    }
);
